import React, { useState } from 'react';
import { t } from 'i18next';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import Backdrop from '@mui/material/Backdrop';
import Fade from '@mui/material/Fade';
import Slide from '@mui/material/Slide';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faTrashCan, faLinesLeaning, faPlus, faXmark } from '@fortawesome/free-solid-svg-icons';
import IconPicker, { renderIcon } from './IconPicker';

// ---------- Styled ----------

const StyledTableCell = styled(TableCell)(() => ({
  color: 'var(--text-color)', padding: '4px 10px',
  backgroundColor: 'var(--background-color)', borderBottom: 'var(--border-input)',
}));

const StyledTableRow = styled(TableCell)(() => ({
  color: 'var(--text-color)', padding: '7px',
  backgroundColor: 'var(--btn-user-form)', borderBottom: 'var(--border-input)', fontSize: 13,
}));

const StyledTableCheck = styled(TableCell)(() => ({
  color: 'var(--text-color)', padding: '0 0 0 8px',
  backgroundColor: 'var(--btn-user-form)', borderBottom: 'var(--border-input)', fontSize: 13,
}));

const CONFIRM_STYLE = {
  position: 'absolute' as const, top: '50%', left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400, bgcolor: 'var(--bg-form)',
  border: 'var(--border-input)', borderRadius: '5px', boxShadow: 24, p: 4,
};

// ---------- Field definition system ----------

export type PortalFieldType = 'text' | 'slug' | 'color' | 'boolean' | 'icon' | 'select' | 'number';

const DEFAULT_SWATCHES = ['#AFA9EC', '#5DCAA5', '#F0997B', '#85B7EB', '#ED93B1', '#97C459'];
const toSlug = (v: string) => v.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

export interface PortalFieldDef {
  key: string;
  label: string;
  type: PortalFieldType;
  showInTable?: boolean;   // default: true
  editable?: boolean;      // default: true — shows in add/edit drawer
  defaultValue?: any;
  slugFrom?: string;       // type='slug': auto-generate from another field
  swatches?: string[];     // type='color': custom swatches
  colorFrom?: string;      // type='icon': read color from another field for preview
  options?: { value: string; label: string }[];  // type='select'
  variant?: 'toggle';      // type='boolean': green/red Switch (requires onToggleField prop)
  isDisabled?: (row: any) => boolean;  // disable toggle per row
  render?: (row: any) => React.ReactNode;  // custom cell render, overrides type-based auto-render
  align?: 'left' | 'right' | 'center';
  width?: string | number;
}

function renderFieldCell(field: PortalFieldDef, row: any): React.ReactNode {
  if (field.render) return field.render(row);
  const val = row[field.key];
  switch (field.type) {
    case 'text':    return <span>{val}</span>;
    case 'number':  return <span>{val}</span>;
    case 'slug':    return <span className="text-[12px] opacity-60 font-mono">{val}</span>;
    case 'color':   return <div className="w-[14px] h-[14px] rounded-full" style={{ backgroundColor: val ?? '#ccc' }} />;
    case 'boolean': return (
      <span className={val ? 'text-green-400' : 'text-[var(--text-color-calendar-week)]'}>
        {val ? '●' : '○'}
      </span>
    );
    case 'icon':    return renderIcon(val, { fontSize: 14 });
    case 'select': {
      return <span>{val}</span>;
    }
    default: return <span>{String(val ?? '')}</span>;
  }
}

function renderFieldEdit(
  field: PortalFieldDef,
  values: Record<string, any>,
  setValues: React.Dispatch<React.SetStateAction<Record<string, any> | null>>,
  allFields: PortalFieldDef[],
): React.ReactNode {
  const val = values[field.key];
  const onChange = (v: any) =>
    setValues((prev) => (prev ? { ...prev, [field.key]: v } : prev));

  switch (field.type) {
    case 'text':
      return (
        <input
          autoFocus
          title={field.label} placeholder={field.label}
          value={val ?? ''}
          onChange={(e) => {
            onChange(e.target.value);
            allFields.forEach((f) => {
              if (f.type === 'slug' && f.slugFrom === field.key)
                setValues((prev) => prev ? { ...prev, [f.key]: toSlug(e.target.value) } : prev);
            });
          }}
          className="w-full text-[13px] py-[4px] px-[6px] rounded-[4px] bg-[var(--bg-input)] text-[var(--text-color)] timesheet-edit-input"
        />
      );
    case 'slug':
      return (
        <input
          title={field.label} placeholder="slug"
          value={val ?? ''}
          onChange={(e) => onChange(toSlug(e.target.value))}
          className="w-full text-[12px] py-[4px] px-[6px] rounded-[4px] font-mono bg-[var(--bg-input)] text-[var(--text-color)] timesheet-edit-input"
        />
      );
    case 'color': {
      const swatches = field.swatches ?? DEFAULT_SWATCHES;
      return (
        <div className="flex gap-[6px] flex-wrap">
          {swatches.map((c) => (
            <button key={c} type="button" title={c} onClick={() => onChange(c)}
              className="w-[22px] h-[22px] rounded-full flex-shrink-0 transition-transform hover:scale-110"
              style={{ backgroundColor: c, outline: val === c ? `2px solid ${c}` : '2px solid transparent', outlineOffset: 2 }}
            />
          ))}
        </div>
      );
    }
    case 'boolean':
      return (
        <input type="checkbox" title={field.label} checked={!!val}
          onChange={(e) => onChange(e.target.checked)}
          className="cursor-pointer w-[16px] h-[16px]" />
      );
    case 'icon': {
      const color = field.colorFrom ? (values[field.colorFrom] ?? '#6366f1') : '#6366f1';
      return <IconPicker value={val} onChange={onChange} color={color} />;
    }
    case 'number':
      return (
        <input type="number" title={field.label} placeholder="0"
          value={val ?? ''}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-full text-[13px] py-[4px] px-[6px] rounded-[4px] bg-[var(--bg-input)] text-[var(--text-color)] timesheet-edit-input" />
      );
    case 'select':
      return (
        <select title={field.label} value={val ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className="edit-select w-full p-[5px] rounded-[4px] edit-input text-[13px]">
          {(field.options ?? []).map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      );
    default: return null;
  }
}

// ---------- Legacy column system (for tables with custom renders) ----------

export interface PortalTableColumn<T> {
  key: string;
  label: string;
  render: (row: T) => React.ReactNode;
  renderEdit?: (
    value: any,
    onChange: (v: any) => void,
    allValues: Record<string, any>,
    setAllValues: React.Dispatch<React.SetStateAction<Record<string, any> | null>>,
  ) => React.ReactNode;
  defaultValue?: any;
  align?: 'left' | 'right' | 'center';
  width?: string | number;
  hideInTable?: boolean;
}

// ---------- Props ----------

export interface PortalTableProps<T> {
  rows: T[];
  getKey: (row: T) => string | number;

  // New declarative API:
  fields?: PortalFieldDef[];
  // Legacy custom-render API:
  columns?: PortalTableColumn<T>[];

  title?: string;
  onAdd?: () => void;
  addLabel?: string;
  onInlineAdd?: (data: Record<string, any>) => Promise<void>;
  inlineAddDefaults?: Record<string, any>;

  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  canDelete?: (row: T) => boolean;
  extraRowActions?: (row: T) => React.ReactNode;
  onToggleField?: (row: T, fieldKey: string) => void;

  onDeleteMany?: (keys: (string | number)[]) => Promise<void>;

  getRowOpacity?: (row: T) => number;

  emptyText?: string;
  showFooterCount?: boolean;
}

// ---------- Component ----------

const TOGGLE_SX = {
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#22c55e', opacity: 1 },
  '& .MuiSwitch-track': { backgroundColor: '#ef4444', opacity: 1 },
  '& .MuiSwitch-switchBase.Mui-checked': { color: '#fff' },
  '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': { opacity: 0.3 },
  '& .MuiSwitch-switchBase.Mui-disabled': { color: '#ccc' },
};

export default function PortalTable<T>({
  rows, getKey,
  fields, columns,
  title, onAdd, addLabel, onInlineAdd, inlineAddDefaults,
  onEdit, onDelete, canDelete, extraRowActions, onToggleField,
  onDeleteMany,
  getRowOpacity,
  emptyText, showFooterCount = true,
}: PortalTableProps<T>) {
  const [selected, setSelected] = useState<(string | number)[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerValues, setDrawerValues] = useState<Record<string, any> | null>(null);
  const [saving, setSaving] = useState(false);

  const hasActions = !!(onEdit || onDelete || extraRowActions);
  const hasMultiSelect = !!onDeleteMany;

  // Derive display/edit sets from whichever API is used
  const tableFields  = fields ? fields.filter((f) => f.showInTable !== false) : null;
  const editableFields = fields ? fields.filter((f) => f.editable !== false) : null;
  const tableColumns = columns ? columns.filter((c) => !c.hideInTable) : null;
  const editableColumns = columns ? columns.filter((c) => c.renderEdit) : null;

  const deletableKeys = hasMultiSelect
    ? rows.filter((r) => (canDelete ? canDelete(r) : true)).map(getKey)
    : [];

  // Only show checkbox column when there is at least one deletable row
  const showCheckboxColumn = hasMultiSelect && deletableKeys.length > 0;

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelected(e.target.checked ? deletableKeys : []);
  };

  const handleSelectRow = (key: string | number, deletable: boolean) => {
    if (!deletable) return;
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const handleDeleteConfirm = async () => {
    if (!onDeleteMany) return;
    setDeleting(true);
    try {
      await onDeleteMany(selected);
      setSelected([]);
      setConfirmOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleAddClick = () => {
    if (onInlineAdd) {
      let defaults: Record<string, any> = { ...(inlineAddDefaults ?? {}) };
      if (fields) {
        defaults = fields.reduce(
          (acc, f) => ({ ...acc, [f.key]: f.defaultValue ?? '' }),
          defaults,
        );
      } else if (columns) {
        defaults = columns.reduce(
          (acc, col) => ({ ...acc, [col.key]: col.defaultValue ?? '' }),
          defaults,
        );
      }
      setDrawerValues(defaults);
      setDrawerOpen(true);
    } else {
      onAdd?.();
    }
  };

  const handleDrawerSave = async () => {
    if (!drawerValues || !onInlineAdd) return;
    setSaving(true);
    try {
      await onInlineAdd(drawerValues);
      setDrawerOpen(false);
      setDrawerValues(null);
    } catch {
      // error handled by caller
    } finally {
      setSaving(false);
    }
  };

  const handleDrawerClose = () => {
    if (saving) return;
    setDrawerOpen(false);
    setDrawerValues(null);
  };

  const allDeletableSelected =
    deletableKeys.length > 0 && deletableKeys.every((k) => selected.includes(k));
  const someDeletableSelected =
    selected.length > 0 && !allDeletableSelected;

  const hasDrawer = onInlineAdd && ((editableFields?.length ?? 0) > 0 || (editableColumns?.length ?? 0) > 0);

  return (
    <div className={`relative mb-[16px] ${showFooterCount ? 'pb-[35px]' : ''}`}>
      {(title || onAdd || onInlineAdd) && (
        <div className="flex justify-between items-center mb-[8px] mt-[4px]">
          {title && (
            <span className="text-[12px] font-medium uppercase tracking-widest text-[var(--text-color)] opacity-50">
              {title}
            </span>
          )}
          {(onAdd || onInlineAdd) && (
            <button
              type="button"
              title={addLabel ?? title}
              onClick={handleAddClick}
              className="h-[25px] px-[10px] rounded-[6px] bg-transparent text-[var(--text-color)] flex items-center justify-center hover:bg-[var(--btn-user-form)] flex-shrink-0 transition-colors"
              style={{ border: '1px solid rgba(var(--shodow), 0.35)' }}>
              <FontAwesomeIcon icon={faPlus} style={{ fontSize: 10 }} className="opacity-50" />
            </button>
          )}
        </div>
      )}

      {rows.length > 0 ? (
        <TableContainer component={Paper} sx={{ boxShadow: 'none', backgroundColor: 'inherit' }}>
          <Table>
            <TableHead>
              <TableRow>
                {showCheckboxColumn && (
                  <StyledTableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      color="primary"
                      indeterminate={someDeletableSelected}
                      checked={allDeletableSelected}
                      onChange={handleSelectAll}
                      inputProps={{ 'aria-label': 'select all' }}
                      sx={{ p: '2px' }}
                    />
                  </StyledTableCell>
                )}
                {tableFields
                  ? tableFields.map((f, i) => (
                      <StyledTableCell key={f.key} align={f.align ?? ('left')} width={f.width}>
                        {f.label}
                      </StyledTableCell>
                    ))
                  : tableColumns!.map((col, i) => (
                      <StyledTableCell key={col.key} align={col.align ?? ('left')} width={col.width}>
                        {col.label}
                      </StyledTableCell>
                    ))
                }
                {hasActions && (
                  <StyledTableCell align="right" width={70}>{t('Edit')}</StyledTableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const key = getKey(row);
                const opacity = getRowOpacity ? getRowOpacity(row) : 1;
                const deletable = canDelete ? canDelete(row) : true;
                const isSelected = selected.includes(key);
                return (
                  <TableRow key={key} className="hover:bg-[var(--none-button)]" sx={{ opacity }}>
                    {showCheckboxColumn && (
                      <StyledTableCheck padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isSelected}
                          disabled={!deletable}
                          onChange={deletable ? () => handleSelectRow(key, deletable) : undefined}
                        />
                      </StyledTableCheck>
                    )}
                    {tableFields
                      ? tableFields.map((f, i) => (
                          <StyledTableRow key={f.key} align={f.align ?? ('left')} width={f.width}>
                            {f.type === 'boolean' && f.variant === 'toggle'
                              ? <Switch size="small"
                                  checked={!!(row as any)[f.key]}
                                  disabled={f.isDisabled?.(row)}
                                  onChange={f.isDisabled?.(row) ? undefined : () => onToggleField?.(row, f.key)}
                                  sx={TOGGLE_SX} />
                              : renderFieldCell(f, row)
                            }
                          </StyledTableRow>
                        ))
                      : tableColumns!.map((col, i) => (
                          <StyledTableRow key={col.key} align={col.align ?? ('left')} width={col.width}>
                            {col.render(row)}
                          </StyledTableRow>
                        ))
                    }
                    {hasActions && (
                      <StyledTableRow align="right">
                        {extraRowActions?.(row)}
                        {onEdit && (
                          <FontAwesomeIcon
                            icon={faPenToSquare}
                            className="text-[14px] cursor-pointer opacity-50 hover:opacity-70 mx-2"
                            onClick={() => onEdit(row)}
                          />
                        )}
                        {onDelete && (
                          <FontAwesomeIcon
                            icon={faTrashCan}
                            className={`text-[14px] opacity-50 hover:opacity-70 ${deletable ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                            onClick={() => deletable && onDelete(row)}
                          />
                        )}
                      </StyledTableRow>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <div className="flex items-center justify-center mt-[20px]">
          <div className="opacity-45">{emptyText ?? t('Empty')}</div>
        </div>
      )}

      {showFooterCount && (
        <div
          className="absolute bottom-0 left-0 p-[5px] flex items-center w-full rounded-b-[5px] px-[10px]"
          style={{
            backgroundColor: selected.length > 0 ? 'rgba(25, 118, 210, 0.12)' : 'var(--bg-top-fix)',
          }}>
          {selected.length > 0 ? (
            <>
              <div className="flex-1 text-[13px] text-[var(--text-color)]">
                {selected.length} {t('Selected')}
              </div>
              <FontAwesomeIcon
                icon={faTrashCan}
                className="text-[16px] cursor-pointer opacity-50 text-[var(--text-color)] hover:opacity-70"
                onClick={() => setConfirmOpen(true)}
              />
            </>
          ) : (
            <div className="flex flex-1 justify-end gap-[5px] items-center text-[16px]" style={{ marginLeft: 5 }}>
              <span className="user-count opacity-70">{rows.length}</span>
              <FontAwesomeIcon icon={faLinesLeaning} className="text-[16px] opacity-30 text-[var(--text-color)]" />
            </div>
          )}
        </div>
      )}

      {/* Bulk delete confirm */}
      <Modal open={confirmOpen} onClose={() => !deleting && setConfirmOpen(false)}
        closeAfterTransition slots={{ backdrop: Backdrop }} slotProps={{ backdrop: { timeout: 500 } }}>
        <Fade in={confirmOpen}>
          <Box sx={CONFIRM_STYLE}>
            <div className="text-center text-[16px] mb-[16px] text-[var(--text-color)]">
              {t('AreYouSure')}? {selected.length} {t('Selected')}
            </div>
            <div className="flex gap-[10px]">
              <Button variant="contained" sx={{ flex: 1, fontSize: 13 }} disabled={deleting} onClick={handleDeleteConfirm}>
                {deleting ? <CircularProgress size={18} color="inherit" /> : t('Yes')}
              </Button>
              <Button variant="contained" sx={{ flex: 1, fontSize: 13 }} disabled={deleting} onClick={() => setConfirmOpen(false)}>
                {t('Cancel')}
              </Button>
            </div>
          </Box>
        </Fade>
      </Modal>

      {/* Add / edit drawer */}
      {hasDrawer && (
        <Modal open={drawerOpen} onClose={handleDrawerClose}
          closeAfterTransition slots={{ backdrop: Backdrop }}
          slotProps={{ backdrop: { timeout: 200, style: { backgroundColor: 'rgba(0,0,0,0.15)' } } }}>
          <Slide in={drawerOpen} direction="left">
            <Box sx={{
              position: 'fixed', right: 0, top: 0, height: '100vh', width: 300,
              bgcolor: 'var(--bg-form)', borderLeft: 'var(--border-input)',
              boxShadow: '-8px 0 24px rgba(0,0,0,0.12)',
              p: 3, display: 'flex', flexDirection: 'column', outline: 'none',
            }}>
              <div className="flex items-center justify-between mb-[20px]">
                <span className="text-[14px] font-medium text-[var(--text-color)]">
                  {addLabel ?? title}
                </span>
                <button type="button" title="Close" onClick={handleDrawerClose}
                  className="opacity-40 hover:opacity-70 transition-opacity">
                  <FontAwesomeIcon icon={faXmark} className="text-[16px] text-[var(--text-color)]" />
                </button>
              </div>

              <div className="flex flex-col gap-[16px] flex-1 overflow-y-auto">
                {drawerValues && fields && editableFields!.map((f) => (
                  <div key={f.key}>
                    <label className="block text-[11px] uppercase tracking-wider text-[var(--text-color)] opacity-50 mb-[6px]">
                      {f.label}
                    </label>
                    {renderFieldEdit(f, drawerValues, setDrawerValues, fields)}
                  </div>
                ))}
                {drawerValues && columns && editableColumns!.map((col) => (
                  <div key={col.key}>
                    <label className="block text-[11px] uppercase tracking-wider text-[var(--text-color)] opacity-50 mb-[6px]">
                      {col.label}
                    </label>
                    {col.renderEdit!(
                      drawerValues[col.key],
                      (v) => setDrawerValues((prev) => prev ? { ...prev, [col.key]: v } : prev),
                      drawerValues,
                      setDrawerValues,
                    )}
                  </div>
                ))}
              </div>

              <div className="flex gap-[8px] mt-[20px]">
                <Button variant="contained" sx={{ flex: 1, fontSize: 13 }} disabled={saving} onClick={handleDrawerSave}>
                  {saving ? <CircularProgress size={16} color="inherit" /> : t('Add')}
                </Button>
                <Button variant="outlined" sx={{ flex: 1, fontSize: 13 }} disabled={saving} onClick={handleDrawerClose}>
                  {t('Cancel')}
                </Button>
              </div>
            </Box>
          </Slide>
        </Modal>
      )}
    </div>
  );
}
